import { forwardRef } from 'react';
import { Assert } from '@team/assert';
import { clsx } from '@team/clsx';
import { ReactKit } from '@team/react';
import { Tappable } from '../../services/tappable';
import { Loader } from './components/loader';
import { Typography } from './components/typography';
import { Props } from './types';
import { LoadingType, Mode, Size } from './enums';
import {
  modePresets,
  sizePresets,
  hoverModePresets,
  activeModePresets,
  detectComponent,
} from './model';
import css from './style.module.css';

export const Layout = forwardRef<HTMLElement, Props>(function Component(
  {
    mode = Mode.FILLED,
    size = Size.SMALL,
    shouldStretched = false,
    isLoading = false,
    loadingType = LoadingType.SPINNER,
    onClick,
    children,
    before,
    after,
    ...props
  },
  forwardedRef
) {
  const hasContent = ReactKit.hasNode(children);
  const hasBefore = ReactKit.hasNode(before);
  const hasAfter = ReactKit.hasNode(after);
  const hasIconOnly = !hasContent && hasBefore !== hasAfter;

  if (import.meta.env.DEV) {
    /**
     * 💬 В случае, когда в компонент явно не передан текст,
     * но при этом есть хотя бы одна иконка,
     * тогда мы должны проверить наличие одно из aria-атрибутов:
     * – aria-label
     * – aria-labelledby
     */
    if (!hasContent && (hasBefore || hasAfter)) {
      if (
        [props['aria-label'], props['aria-labelledby']].every(
          Assert.isUndefined
        )
      ) {
        console.warn(
          '[TelegramUi.Button]: a11y police. Отсутствует один из пропсов: `aria-label` или `aria-labelledby`'
        );
      }
    }
  }

  return (
    <Tappable
      {...props}
      Component={detectComponent(props)}
      baseClassName={clsx(css.root, modePresets[mode], sizePresets[size], {
        [css.stretched]: shouldStretched,
        [css.loading]: isLoading,
        [css.onlyIcon]: hasIconOnly && !shouldStretched,
      })}
      hoverMode={hoverModePresets[mode]}
      activeMode={activeModePresets[mode]}
      onClick={isLoading ? undefined : onClick}
      ref={forwardedRef}
    >
      {isLoading && (
        <Loader
          type={loadingType}
          className={clsx(
            css.loader,
            loadingType === LoadingType.SPINNER && css.spinner
          )}
        />
      )}

      <span className={css.inner}>
        {hasBefore && (
          <span className={css.before} role="presentation">
            {before}
          </span>
        )}

        {hasContent && (
          <Typography className={css.content} mode={mode} size={size}>
            {children}
          </Typography>
        )}

        {hasAfter && (
          <span className={css.after} role="presentation">
            {after}
          </span>
        )}
      </span>
    </Tappable>
  );
});
